import React, { useMemo } from 'react';
import { Label } from './ui/label';
import { Users, Target, Hash, UserCheck } from 'lucide-react';
import SearchableAthleteSelect from './SearchableAthleteSelect';

/**
 * Mental-Hub targeting selector (mode: all | group | individual).
 *
 * value: {
 *   mode: "all" | "group" | "individual",
 *   groups?: string[],
 *   athlete_ids?: string[],
 *   parent_scope?: boolean
 * }
 * onChange: (value) => void
 *
 * Props:
 *  - athletes: [{id, full_name, squad}]
 *  - showParentToggle: bool (Mental Hub entries only — not for Parent Insights)
 *  - parentLocked: bool (Parent Insights — parent_scope forced true)
 */
const TargetingField = ({
  athletes = [],
  value = { mode: 'all', groups: [], athlete_ids: [], parent_scope: false },
  onChange,
  label = 'Target',
  showParentToggle = true,
  parentLocked = false,
  'data-testid': testId = 'targeting-field',
}) => {
  const mode = value.mode || 'all';
  const groups = value.groups || [];
  const athleteIds = value.athlete_ids || [];
  const parentScope = parentLocked ? true : !!value.parent_scope;

  const allSquads = useMemo(() => {
    const s = new Set();
    athletes.forEach(a => { if (a.squad) s.add(a.squad); });
    return [...s].sort();
  }, [athletes]);

  const setMode = (newMode) => {
    onChange({
      mode: newMode,
      groups: newMode === 'group' ? groups : [],
      athlete_ids: newMode === 'individual' ? athleteIds : [],
      parent_scope: parentScope,
    });
  };

  const toggleGroup = (squad) => {
    const next = groups.includes(squad) ? groups.filter(g => g !== squad) : [...groups, squad];
    onChange({ ...value, mode: 'group', groups: next, parent_scope: parentScope });
  };

  const setAthletes = (ids) => {
    onChange({ ...value, mode: 'individual', athlete_ids: ids, parent_scope: parentScope });
  };

  const toggleParent = () => {
    if (parentLocked) return;
    onChange({ ...value, parent_scope: !parentScope });
  };

  const modes = [
    { key: 'all', label: 'All', icon: Users },
    { key: 'group', label: 'Group', icon: Hash },
    { key: 'individual', label: 'Individual', icon: UserCheck },
  ];

  return (
    <div data-testid={testId}>
      {label && <Label className="block mb-2 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> {label}</Label>}

      {/* Mode segmented control */}
      <div className="inline-flex items-center rounded-lg border bg-gray-50 dark:bg-gray-800 p-0.5 mb-3" role="tablist">
        {modes.map(m => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                active ? 'bg-white dark:bg-gray-700 text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`${testId}-mode-${m.key}`}
            >
              <Icon className="w-3 h-3" /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Parent toggle */}
      {showParentToggle && (
        <label className="inline-flex items-center gap-2 ml-3 cursor-pointer" data-testid={`${testId}-parent-toggle`}>
          <input
            type="checkbox"
            checked={parentScope}
            onChange={toggleParent}
            disabled={parentLocked}
            className="rounded border-gray-300"
          />
          <span className="text-xs text-gray-600 dark:text-gray-400">Target parents instead of athletes</span>
        </label>
      )}

      {/* Group picker */}
      {mode === 'group' && (
        <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`${testId}-groups`}>
          {allSquads.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No squads available</p>
          ) : allSquads.map(sq => (
            <button
              key={sq}
              type="button"
              onClick={() => toggleGroup(sq)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                groups.includes(sq)
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`${testId}-group-${sq}`}
            >
              {sq}
            </button>
          ))}
        </div>
      )}

      {/* Individual picker */}
      {mode === 'individual' && (
        <div className="mt-1" data-testid={`${testId}-athletes`}>
          <SearchableAthleteSelect
            athletes={athletes}
            value={athleteIds}
            onChange={setAthletes}
            multi
            placeholder="Search & select specific athletes..."
            data-testid={`${testId}-athlete-select`}
          />
        </div>
      )}

      {mode === 'all' && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1" data-testid={`${testId}-summary-all`}>
          {parentScope ? 'Visible to all parents' : 'Visible to all athletes'}
        </p>
      )}
    </div>
  );
};

export default TargetingField;
